import React from "react"
import merge from "lodash/merge"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as callouts from "activities/components/callouts"
import * as refinance from "activities/components/refinance"
import AccessibilityTable from "activities/components/shared/accessibility-table"

const RefinanceLoan_ = shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const {balance, currentMonthly, currentRate, refinanceTerm, refinanceRate} = getState()
    const isDisabled = currentMonthly == 0
    const {data, interestCurrent, interestRefinance, refinanceMonthly} = refinance.validateData(isDisabled, () =>
      refinance.graphDataAuto(balance, currentMonthly, currentRate / 100, refinanceTerm, refinanceRate / 100)
    )

    const accessibilityTableHeadings = [
      {name: "Years", key: "years"},
      {name: "Current", key: "current"},
      {name: "Refinance", key: "refinance"}
    ]

    const accessibilityTableData = refinance.getYears(data).map((row) => ({
      years: row[0],
      current: util.formatDollarsNoCents(row[1]),
      refinance: util.formatDollarsNoCents(row[2])
    }))

    const accessibilityTableLabel = "Current VS Refinance Table"

    return (
      <div>
        <shared.SummaryCalcContainer>
          <shared.InputsContainer>
            <refinance.Inputs props={getProps()} state={getState()} updateState={updateState} />
          </shared.InputsContainer>
          <shared.ChartContainer>
            <refinance.Graph props={getProps()} graphData={data} isDisabled={isDisabled} />
          </shared.ChartContainer>
          <shared.SummaryContainer>
            <callouts.Callout>
              <callouts.Detail label="Months to Payoff" result={refinanceTerm} dataTestId="months-payoff" />
              <callouts.Detail
                label="New Monthly Payment"
                result={refinanceMonthly === Infinity ? "$Infinity" : util.formatDollarsNoCents(util.ifNegative(Number(refinanceMonthly)))}
                dataTestId="new-payment"
              />
              <callouts.Detail
                label="Interest Saved"
                result={util.formatDollarsNoCents(util.ifNegative(Number(interestCurrent - interestRefinance)))}
                dataTestId="interest-saved"
              />
            </callouts.Callout>
          </shared.SummaryContainer>
        </shared.SummaryCalcContainer>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {balance, currentMonthly, currentRate, refinanceTerm, refinanceRate} = state
      const isDisabled = currentMonthly == 0
      const _currentRate = currentRate / 100
      const _refinanceRate = refinanceRate / 100
      const {interestCurrent, interestRefinance, refinanceMonthly} = refinance.validateData(isDisabled, () =>
        refinance.graphDataAuto(balance, currentMonthly, _currentRate, refinanceTerm, _refinanceRate)
      )
      return Object.assign(state, {
        balance,
        currentMonthly,
        currentRate: _currentRate,
        refinanceTerm,
        refinanceRate: _refinanceRate,
        refinanceMonthly,
        interestSaved: util.ifNegative(Number(interestCurrent - interestRefinance))
      })
    },
    defaultProps: {
      id: "refinance-loan",
      className: "early-payoff",
      title: "Loan Refinance Calculator",
      subtitle: "Compare a refinanced loan against your current loan.",
      loanTerm: {
        min: 1,
        max: 10,
        step: 1
      },
      interestRate: {
        min: 1.5,
        max: 8,
        step: 0.05
      }
    },
    defaultState: {
      balance: 1000,
      currentMonthly: 0,
      currentRate: 4.75,
      refinanceTerm: 5,
      refinanceRate: 4.75
    }
  }
)

export default (props) => (
  <RefinanceLoan_
    {...merge(
      {
        id: "refinance-auto-loan-term",
        title: "Auto Loan Refinance (Term)",
        subtitle: "See how refinancing can help you pay off your auto loan early and save on interest!",
        loanTerm: {
          min: 1,
          max: 120,
          step: 1
        },
        interestRate: {
          min: 0,
          max: 20,
          step: 0.01
        },
        initialState: {
          balance: 10000,
          currentMonthly: 300,
          currentRate: 5,
          refinanceTerm: 24,
          refinanceRate: 3.69
        },
        ariaLabel:
          "This line graph visually compares a refinanced auto loan against your current auto loan. Read below to find the refinance monthly payment, and the refiance remaining interest."
      },
      props
    )}
  />
)
